import {
  Rocket,
  FileText,
  ClipboardCheck,
  AlertTriangle,
  FileBarChart,
  BarChart3,
  Users,
  FileWarning,
  Layers,
  Gauge,
  Target,
  Trash2,
  Timer,
} from "lucide-react";
import { KPICard } from "@/core/components/dashboard/KPICard";
import { ModuleCard } from "@/core/components/dashboard/ModuleCard";
import { AlertsPanel } from "@/core/components/dashboard/AlertsPanel";
import { ProductionChart } from "@/core/components/dashboard/ProductionChart";
import { QuickActions } from "@/core/components/dashboard/QuickActions";
import { OperatorDashboard } from "@/core/components/dashboard/OperatorDashboard";
import { useLanguage } from "@/core/LanguageContext";
import { useAppScope } from "@/core/AppScopeContext";

const kpis = [
  {
    key: "oee",
    title: "OEE (Overall Equipment Effectiveness)",
    value: "87.3",
    unit: "%",
    trend: { value: 2.1, isPositive: true },
    icon: Gauge,
    status: "good" as const,
  },
  {
    key: "fpy",
    title: "FPY (First Pass Yield)",
    value: "96.8",
    unit: "%",
    trend: { value: 0.5, isPositive: true },
    icon: Target,
    status: "good" as const,
  },
  {
    key: "scrap",
    title: "Scrap Rate",
    value: "1.2",
    unit: "%",
    trend: { value: 0.3, isPositive: true },
    icon: Trash2,
    status: "good" as const,
  },
  {
    key: "cycleTime",
    title: "Avg. Cycle Time",
    value: "42.5",
    unit: "min",
    trend: { value: 1.8, isPositive: false },
    icon: Timer,
    status: "warning" as const,
  },
];

const modules = [
  {
    key: "startup",
    title: "Digital Startup",
    description: "Equipment startup work instructions and verification",
    icon: Rocket,
    href: "/startup",
    stats: { label: "Pending startups", value: "3" },
    status: "active" as const,
  },
  {
    key: "instructions",
    title: "Work Instructions",
    description: "Startup work instructions and verification",
    icon: FileText,
    href: "/instructions",
    stats: { label: "Active work instructions", value: "24" },
    status: "active" as const,
  },
  {
    key: "qualityChecks",
    title: "Quality Checks",
    description: "In-process and final inspection workflows",
    icon: ClipboardCheck,
    href: "/quality-checks",
    stats: { label: "Checks today", value: "47" },
    status: "active" as const,
  },
  {
    key: "defects",
    title: "Defect Tagging",
    description: "Digital defect capture and tracking",
    icon: AlertTriangle,
    href: "/defects",
    stats: { label: "Open defects", value: "8" },
    status: "pending" as const,
  },
  {
    key: "shiftReports",
    title: "Shift Reports",
    description: "Digital handover and shift documentation",
    icon: FileBarChart,
    href: "/shift-reports",
    stats: { label: "Reports today", value: "2" },
    status: "active" as const,
  },
  {
    key: "performance",
    title: "Performance View",
    description: "OEE, FPY, scrap, and cycle time analytics",
    icon: BarChart3,
    href: "/performance",
    stats: { label: "Reports generated", value: "156" },
    status: "idle" as const,
  },
  {
    key: "competency",
    title: "Competency Management",
    description: "Skills matrix and training tracking",
    icon: Users,
    href: "/competency",
    stats: { label: "Active employees", value: "42" },
    status: "idle" as const,
  },
  {
    key: "eightD",
    title: "8D Management",
    description: "Supplier quality and corrective actions",
    icon: FileWarning,
    href: "/8d-management",
    stats: { label: "Open 8Ds", value: "5" },
    status: "pending" as const,
  },
  {
    key: "audits",
    title: "Process Audits",
    description: "Layered process audit management",
    icon: Layers,
    href: "/audits",
    stats: { label: "Audits this month", value: "12" },
    status: "idle" as const,
  },
];

export default function Dashboard() {
  const { t } = useLanguage();
  const { effectiveMode } = useAppScope();

  // Show operator dashboard for operator mode
  if (effectiveMode === 'operator') {
    return <OperatorDashboard />;
  }

  const localizedKpis = kpis.map(kpi => ({
    ...kpi,
    title: t(`dashboard.kpi.${kpi.key}.title`, kpi.title),
  }));

  const localizedModules = modules.map(module => ({
    ...module,
    title: t(`dashboard.module.${module.key}.title`, module.title),
    description: t(`dashboard.module.${module.key}.description`, module.description),
    stats: module.stats
      ? {
          ...module.stats,
          label: t(`dashboard.module.${module.key}.stats`, module.stats.label),
        }
      : undefined,
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("dashboard.title", "Quality Dashboard")}</h1>
        <p className="text-muted-foreground mt-1">
          {t("dashboard.subtitle", "Welcome back! Here's your production quality overview for today.")}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {localizedKpis.map((kpi) => (
          <KPICard key={kpi.title} {...kpi} trendLabel={t("dashboard.kpi.trend", "vs last shift")} />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart & Quick Actions */}
        <div className="lg:col-span-2 space-y-6">
          <ProductionChart />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <QuickActions />
          <AlertsPanel />
        </div>
      </div>

      {/* Modules Grid */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {t("dashboard.modulesHeading", "Quality Modules")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {localizedModules.map((module) => (
            <ModuleCard key={module.title} {...module} />
          ))}
        </div>
      </div>
    </div>
  );
}
